import { parseBooleanEnvFlag } from './env.js'

export const ADSENSE_PUBLISHER_ID = 'ca-pub-9395001390823290'
export const EDITORIAL_AUTO_AD_PAGES = [
  '/baixar-visualg', '/aprender/exercicios-portugol-resolvidos-visualg',
  '/o-que-e-portugol', '/o-que-e-visualg', '/o-que-e-programacao',
  '/como-aprender-python', '/o-que-e-linguagem-de-programacao',
]
export const AD_EXPERIMENT_IDS = [
  'download_format', 'editorial_responsive', 'learning_density', 'exercises_density',
  'editorial_side_rail', 'nexo_result', 'editor_variables', 'auto_anchors', 'auto_in_page',
]
const UNIT_ENV = {
  downloadRectangle: 'VITE_GAM_DOWNLOAD_RECTANGLE_AD_UNIT_PATH',
  downloadResponsive: 'VITE_GAM_DOWNLOAD_RESPONSIVE_AD_UNIT_PATH',
  editorialResponsive: 'VITE_GAM_SEO_CONTENT_ADAPTIVE_AD_UNIT_PATH',
  editorialSideRail: 'VITE_GAM_EDITORIAL_SIDE_RAIL_AD_UNIT_PATH',
  nexoResult: 'VITE_GAM_NEXO_RESULT_AD_UNIT_PATH',
  editorVariables: 'VITE_GAM_EDITOR_VARIABLES_AD_UNIT_PATH',
}
export const validGamPath = value => /^\/\d+\/[a-zA-Z0-9_/-]+$/.test(String(value || '')) ? value : ''
const flag = value => parseBooleanEnvFlag(value, false)

// This schema is an allowlist. Runtime JSON cannot add routes, scripts or providers.
export function normalizeAdsConfig(raw = {}, env = import.meta.env) {
  const experiments = Object.fromEntries(AD_EXPERIMENT_IDS.map(id => {
    const source = raw.experiments?.[id] || {}
    const enabled = source.enabled ?? env[`VITE_AD_EXPERIMENT_${id.toUpperCase()}`]
    const percent = Number(source.treatmentPercent ?? env.VITE_AD_EXPERIMENT_TREATMENT_PERCENT ?? 10)
    return [id, { enabled: flag(enabled), treatmentPercent: Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0 }]
  }))
  const auto = raw.autoAds || {}
  const pages = auto.pages ?? String(env.VITE_AUTO_ADS_PAGES || '').split(',')
  const profile = auto.profile ?? env.VITE_AUTO_ADS_REVIEWED_PROFILE
  return {
    version: String(raw.version || 'baseline').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 40),
    experiments,
    downloadFormat: (raw.downloadFormat ?? env.VITE_AD_DOWNLOAD_FORMAT) === 'responsive' ? 'responsive' : 'rectangle',
    units: Object.fromEntries(Object.entries(UNIT_ENV).map(([name, key]) => [name, validGamPath(raw.units?.[name] ?? env[key])])),
    autoAds: {
      editorial: flag(auto.editorial ?? env.VITE_AUTO_ADS_EDITORIAL),
      anchors: flag(auto.anchors ?? env.VITE_AUTO_ADS_ANCHORS),
      inPage: flag(auto.inPage ?? env.VITE_AUTO_ADS_IN_PAGE),
      // Recognized for diagnostics; enabling it blocks this selective rollout.
      vignettes: flag(auto.vignettes ?? env.VITE_AUTO_ADS_VIGNETTES),
      configurationVerified: flag(auto.configurationVerified ?? env.VITE_AUTO_ADS_CONFIGURATION_VERIFIED),
      profile: ['anchors', 'in_page'].includes(profile) ? profile : 'none',
      pages: Array.isArray(pages) ? pages.filter(page => EDITORIAL_AUTO_AD_PAGES.includes(page)) : [],
    },
  }
}

export const DEFAULT_ADS_CONFIG = normalizeAdsConfig()
let configurationPromise
export function loadAdsConfig(env = import.meta.env) {
  const path = env.VITE_ADS_RUNTIME_CONFIG_URL
  // Disabled unless explicitly configured. Only same-origin JSON is accepted.
  if (!/^\/(?!\/)[a-zA-Z0-9_/-]+\.json$/.test(path || '')) return Promise.resolve(normalizeAdsConfig({}, env))
  if (!configurationPromise) configurationPromise = (async () => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 2500)
    try {
      const response = await fetch(path, { signal: controller.signal, cache: 'no-store', credentials: 'omit' })
      if (!response.ok) throw new Error('ads_config_unavailable')
      const raw = await response.json()
      if (!raw || Array.isArray(raw) || typeof raw.version !== 'string' || !raw.experiments || typeof raw.experiments !== 'object') throw new Error('invalid_ads_config')
      return normalizeAdsConfig(raw, env)
    } catch {
      // A failed runtime rollout must not silently enable a build-time experiment.
      const config = normalizeAdsConfig({}, env)
      for (const experiment of Object.values(config.experiments)) experiment.enabled = false
      config.autoAds.editorial = false
      return config
    } finally { clearTimeout(timer) }
  })()
  return configurationPromise
}
